import { useState, useEffect, Fragment } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronDown, ChevronRight, FileText } from "lucide-react";
import api, { handleApiError, formatCurrency } from "@/lib/api";
import { toast } from "sonner";

export default function EmployeeStatement({ employeeId, employeeName, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedMonth, setExpandedMonth] = useState(null);

  useEffect(() => {
    if (!open || !employeeId) return;
    setLoading(true);
    api.get(`/employees/${employeeId}/statement?months=12`)
      .then(res => setData(res.data))
      .catch((err) => toast.error(handleApiError(err, "Erro ao carregar extrato")))
      .finally(() => setLoading(false));
  }, [open, employeeId]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl border-white/10 max-h-[85vh] overflow-y-auto" data-testid="employee-statement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-400" /> Extrato — {employeeName}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12"><div className="loading-spinner w-8 h-8" /></div>
        ) : data ? (
          <div className="space-y-2 py-2">
            <div className="text-xs text-muted-foreground mb-3">
              {data.employee.type} · {data.employee.department || "Sem departamento"} · Admissão: {data.employee.hire_date || "-"}
            </div>

            <div className="flex flex-wrap gap-3 p-3 rounded-lg bg-black/20 border border-white/5 mb-2">
              <div className="flex items-center gap-1.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-400">Pago</span><span className="text-[10px] text-muted-foreground">Fechado e pago</span></div>
              <div className="flex items-center gap-1.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-500/15 text-blue-400">Fechado</span><span className="text-[10px] text-muted-foreground">Aguardando pagamento</span></div>
              <div className="flex items-center gap-1.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-yellow-500/15 text-yellow-400">Atual</span><span className="text-[10px] text-muted-foreground">Mês vigente</span></div>
              <div className="flex items-center gap-1.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-white/5 text-muted-foreground">Aberto</span><span className="text-[10px] text-muted-foreground">Ainda não fechado</span></div>
              <div className="flex items-center gap-1.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-500/15 text-purple-400 opacity-50">Previsão</span><span className="text-[10px] text-muted-foreground">Valores estimados</span></div>
            </div>

            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground w-8"></TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Mês</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Bruto</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Descontos</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Acréscimos</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Antecipações</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Líquido</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.months.map((m) => {
                  const isExpanded = expandedMonth === m.month;
                  const hasItems = m.discounts?.length > 0 || m.anticipation_count > 0;
                  const isForecast = m.status === "forecast";
                  const statusBadge = {
                    paid: { label: "Pago", cls: "bg-emerald-500/15 text-emerald-400" },
                    closed: { label: "Fechado", cls: "bg-blue-500/15 text-blue-400" },
                    current: { label: "Atual", cls: "bg-yellow-500/15 text-yellow-400" },
                    forecast: { label: "Previsão", cls: "bg-purple-500/15 text-purple-400" },
                    open: { label: "Aberto", cls: "bg-white/5 text-muted-foreground" },
                  }[m.status] || { label: m.status, cls: "bg-white/5 text-muted-foreground" };
                  const rowOpacity = isForecast ? "opacity-50" : "";
                  return (
                    <Fragment key={m.month}>
                      <TableRow data-testid={`statement-row-${m.month}`}
                        className={`border-white/5 ${rowOpacity} ${hasItems ? "cursor-pointer hover:bg-white/[0.03]" : ""}`}
                        onClick={() => hasItems && setExpandedMonth(isExpanded ? null : m.month)}>
                        <TableCell className="w-8">
                          {hasItems && (isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{m.month_label}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${statusBadge.cls}`}>{statusBadge.label}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(m.gross)}</TableCell>
                        <TableCell className="text-right text-sm text-red-400">{m.total_descontos > 0 ? `-${formatCurrency(m.total_descontos)}` : "-"}</TableCell>
                        <TableCell className="text-right text-sm text-emerald-400">{m.total_acrescimos > 0 ? `+${formatCurrency(m.total_acrescimos)}` : "-"}</TableCell>
                        <TableCell className="text-right text-sm text-blue-400">{m.total_anticipations > 0 ? `-${formatCurrency(m.total_anticipations)}` : "-"}</TableCell>
                        <TableCell className="text-right text-sm font-semibold text-emerald-400">{formatCurrency(m.net_value)}</TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow key={`${m.month}-detail`} className="border-white/5">
                          <TableCell colSpan={7} className="py-0 px-0">
                            <div className="p-4 bg-black/20 space-y-1.5">
                              <p className="text-xs font-semibold text-muted-foreground mb-2">Detalhamento — {m.month_label}</p>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>Salário: {formatCurrency(m.salary)}</span>
                              </div>
                              {m.discounts.map((d, idx) => (
                                <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-white/5 last:border-0">
                                  <div className="flex items-center gap-2">
                                    <span className={`w-1.5 h-1.5 rounded-full ${d.entry_type === "despesa_acessoria" ? "bg-purple-400" : d.entry_type === "acrescimo" ? "bg-emerald-400" : "bg-red-400"}`} />
                                    <span>{d.description}</span>
                                    <span className="text-[10px] text-muted-foreground">
                                      ({d.entry_type === "despesa_acessoria" ? "Desp. Acessória" : d.entry_type === "acrescimo" ? "Acréscimo" : "Desconto"})
                                    </span>
                                  </div>
                                  <span className={d.entry_type === "despesa_acessoria" ? "text-purple-400" : d.entry_type === "acrescimo" ? "text-emerald-400" : "text-red-400"}>
                                    {d.entry_type === "acrescimo" ? "+" : d.entry_type === "desconto" ? "-" : ""}{formatCurrency(Math.abs(d.amount))}
                                  </span>
                                </div>
                              ))}
                              {m.anticipation_count > 0 && (
                                <div className="flex items-center justify-between text-xs py-1">
                                  <div className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                                    <span>Antecipações ({m.anticipation_count}x)</span>
                                  </div>
                                  <span className="text-blue-400">-{formatCurrency(m.total_anticipations)}</span>
                                </div>
                              )}
                              {m.total_despesas_acessorias > 0 && (
                                <div className="flex items-center justify-between text-xs pt-2 border-t border-white/10">
                                  <span className="text-purple-400 font-medium">Despesas Acessórias (custo empresa)</span>
                                  <span className="text-purple-400">{formatCurrency(m.total_despesas_acessorias)}</span>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
